import React, { useEffect, useState } from "react";
import "./CustomTableWrapper.css";

const CustomTableWrapper = ({
  data = [],
  itemsPerPage = 10,
  columns = [],
  renderRow,
  enableSearch = false,
  searchKeys = [],
  title = "Data Table",
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(itemsPerPage);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Sanitize data to avoid non-array issues
  const sanitizedData = Array.isArray(data) ? data : [];

//   if (sanitizedData.length === 0) {
//   console.warn("⚠️ No data passed to table. Using test data temporarily.");
//   sanitizedData.push({
//     invoice_no: "#123",
//     invoice_Type: "Test",
//     companyName: "Kapil Inc",
//     invoice_date: "2025-08-01",
//     invoice_due_date: "2025-08-10",
//     Total_amount: 1000,
//     status: "Paid"
//   });
// }

  const handleSort = (field) => {
    if (!columns.find((col) => col.field === field)?.sortable) return;
    if (field === sortField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (field) => {
    const isActive = field === sortField;
    return (
      <span className="ctw-sort-icon">
        <i className={`bx bx-chevron-up ${isActive && sortOrder === "asc" ? "active" : ""}`}></i>
        <i className={`bx bx-chevron-down ${isActive && sortOrder === "desc" ? "active" : ""}`}></i>
      </span>
    );
  };

  const getSortedData = (dataArray) => {
    if (!Array.isArray(dataArray)) return [];
    if (!sortField) return dataArray;
    return [...dataArray].sort((a, b) => {
      const valA = (a[sortField] || "").toString().toLowerCase();
      const valB = (b[sortField] || "").toString().toLowerCase();
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  const getFilteredData = () => {
    if (!enableSearch || !searchTerm.trim()) return sanitizedData;
    const term = searchTerm.toLowerCase();
    return sanitizedData.filter((item) =>
      searchKeys.some((key) =>
        (item[key] || "").toString().toLowerCase().includes(term)
      )
    );
  };

  const filteredData = getFilteredData();
  const sortedData = getSortedData(filteredData);
  const totalPages = Math.ceil(sortedData.length / currentPageSize);
  const currentData = sortedData.slice(
    (currentPage - 1) * currentPageSize,
    currentPage * currentPageSize
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredData.length, currentPageSize, currentPage, totalPages]);

  return (
    <div className="ctw-shell">
      <div className="ctw-topbar">
        <div className="ctw-title-wrap">
          <h3 className="ctw-title">{title}</h3>
          <p className="ctw-subtitle">{sortedData.length} total records</p>
        </div>

        <div className="ctw-controls">
          <select
            className="form-select form-select-sm ctw-page-size"
            value={currentPageSize}
            onChange={(e) => {
              setCurrentPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>

          {enableSearch && (
            <div className="ctw-search-wrap">
              <i className="bx bx-search ctw-search-icon"></i>
              <input
                type="text"
                className="form-control ctw-search-input"
                placeholder="Search company, email, contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="ctw-table-wrap">
        <table className="table ctw-table mb-0">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  onClick={() => handleSort(col.field)}
                  style={{ cursor: col.sortable ? "pointer" : "default" }}
                >
                  <span className="ctw-head-label">
                    {col.label}
                    {col.sortable && renderSortIcon(col.field)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData && currentData.length > 0 ? (
              currentData.map((item, index) =>
                renderRow(item, index, currentPage, currentPageSize)
              )
            ) : (
              <tr>
                <td colSpan={columns.length} className="ctw-empty">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="ctw-footer">
        <div className="ctw-footer-text">
          Showing{" "}
          {sortedData.length === 0
            ? 0
            : (currentPage - 1) * currentPageSize + 1}{" "}
          to {Math.min(currentPage * currentPageSize, sortedData.length)} of{" "}
          {sortedData.length} entries
        </div>

        <div className="ctw-pagination">
          <button
            className="ctw-page-btn"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="ctw-page-btn"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button className="ctw-page-current" disabled>
            {currentPage}
          </button>
          <button
            className="ctw-page-btn"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>
          <button
            className="ctw-page-btn"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomTableWrapper;
